import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowUpRight,
  Bug,
  Copy,
  FileText,
  Code,
  House,
  Library,
  Moon,
  UserRound,
  Rss,
} from 'lucide-react';
import { blogs, blogPath, isPublished } from '../lib/blogs';
import { profile } from '../lib/profile';
import { toggleTheme } from '../lib/theme';
import './CommandMenu.css';

export const OPEN_EVENT = 'commandmenu:open';

export function openCommandMenu() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

export default function CommandMenu() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [toast, setToast] = useState('');
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const returnFocus = useRef(null);

  const external = (href) => () => window.open(href, '_blank', 'noopener,noreferrer');

  const items = useMemo(
    () => [
      { group: 'Pages', label: 'Home', icon: House, run: () => navigate('/') },
      { group: 'Pages', label: 'Writing', icon: Library, run: () => navigate('/blog') },
      ...blogs.filter(isPublished).map((b) => ({
        group: 'Posts',
        label: b.title,
        icon: FileText,
        run: () => navigate(blogPath(b)),
      })),
      { group: 'Pages', label: 'Vulns', icon: Bug, run: () => navigate('/vulns') },
      { group: 'Links', label: 'GitHub', icon: Code, run: external(profile.links.github), ext: true },
      { group: 'Links', label: 'LinkedIn', icon: UserRound, run: external(profile.links.linkedin), ext: true },
      { group: 'Links', label: 'RSS feed', icon: Rss, run: external('/rss.xml'), ext: true },
      {
        group: 'Actions',
        label: 'Copy email',
        icon: Copy,
        keepOpen: true,
        run: async () => {
          try {
            await navigator.clipboard.writeText(profile.email);
            setToast('Email copied');
          } catch {
            window.location.href = `mailto:${profile.email}`;
          }
        },
      },
      { group: 'Actions', label: 'Toggle theme', icon: Moon, keepOpen: true, run: toggleTheme },
    ],
    [navigate]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => `${i.group} ${i.label}`.toLowerCase().includes(q));
  }, [items, query]);

  const close = useCallback(() => {
    setOpen(false);
    returnFocus.current?.focus?.();
  }, []);

  const show = useCallback(() => {
    returnFocus.current = document.activeElement;
    setQuery('');
    setActive(0);
    setToast('');
    setOpen(true);
  }, []);

  // ⌘K / Ctrl+K anywhere, "/" when not typing
  useEffect(() => {
    const onKey = (e) => {
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName) || e.target.isContentEditable;
      if ((e.key === 'k' && (e.metaKey || e.ctrlKey)) || (e.key === '/' && !typing && !open)) {
        e.preventDefault();
        open ? close() : show();
      }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener(OPEN_EVENT, show);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(OPEN_EVENT, show);
    };
  }, [open, show, close]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => setActive(0), [query]);

  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-index="${active}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  if (!open) return null;

  const runItem = (item) => {
    item.run();
    if (!item.keepOpen) close();
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => (a + 1) % Math.max(filtered.length, 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (a - 1 + filtered.length) % Math.max(filtered.length, 1));
    } else if (e.key === 'Enter' && filtered[active]) {
      e.preventDefault();
      runItem(filtered[active]);
    } else if (e.key === 'Tab') {
      e.preventDefault(); // keep focus inside the dialog
    }
  };

  let lastGroup = null;

  return (
    <div className="cmd" onMouseDown={(e) => e.target === e.currentTarget && close()}>
      <div className="cmd__panel" role="dialog" aria-modal="true" aria-label="Command menu">
        <input
          ref={inputRef}
          className="cmd__input"
          placeholder="Type a command or search…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-expanded="true"
          aria-controls="cmd-list"
          aria-activedescendant={filtered[active] ? `cmd-item-${active}` : undefined}
          spellCheck={false}
          autoComplete="off"
        />
        <ul className="cmd__list" id="cmd-list" role="listbox" ref={listRef}>
          {filtered.length === 0 && <li className="cmd__empty">No results</li>}
          {filtered.map((item, i) => {
            const header = item.group !== lastGroup ? item.group : null;
            lastGroup = item.group;
            const Icon = item.icon;
            return (
              <li key={`${item.group}-${item.label}`} role="presentation">
                {header && <div className="cmd__group">{header}</div>}
                <div
                  id={`cmd-item-${i}`}
                  data-index={i}
                  role="option"
                  aria-selected={i === active}
                  className={`cmd__item${i === active ? ' is-active' : ''}`}
                  onMouseMove={() => setActive(i)}
                  onClick={() => runItem(item)}
                >
                  <Icon size={15} aria-hidden="true" />
                  <span className="cmd__label">{item.label}</span>
                  {item.ext && <ArrowUpRight size={13} className="cmd__ext" aria-hidden="true" />}
                </div>
              </li>
            );
          })}
        </ul>
        <div className="cmd__foot">
          <span>
            <kbd>↑</kbd>
            <kbd>↓</kbd> navigate
          </span>
          <span>
            <kbd>↵</kbd> select
          </span>
          <span>
            <kbd>esc</kbd> close
          </span>
          {toast && (
            <span className="cmd__toast" role="status">
              {toast}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
